/**
 * Security utilities for preventing XSS and other attacks
 */

/**
 * Escapes HTML characters to prevent XSS attacks
 * @param unsafe - The unsafe string that may contain HTML characters
 * @returns Escaped string safe for display
 */
export function escapeHtml(unsafe: string): string {
  if (!unsafe || typeof unsafe !== 'string') {
    return '';
  }

  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Decodes HTML entities to their character equivalents
 * Useful for displaying backend-sanitized text that contains HTML entities
 * @param text - The text containing HTML entities
 * @returns Decoded string with entities converted to characters
 * @example
 * decodeHtmlEntities("It&#x27;s my job.") // Returns: "It's my job."
 * decodeHtmlEntities("&lt;div&gt;") // Returns: "<div>"
 */
export function decodeHtmlEntities(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Use the browser's built-in HTML entity decoding via a temporary element
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Sanitizes a URL to ensure it's safe for use in img src or links
 * @param url - The URL to sanitize
 * @returns Safe URL or empty string if invalid
 */
export function sanitizeUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Allow only http, https, and data URLs
  const allowedProtocols = /^(https?:|data:|\/)/i;

  try {
    // Check if it's a relative URL
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
      return url;
    }

    // For absolute URLs, validate the protocol
    const urlObj = new URL(url);
    if (allowedProtocols.test(urlObj.protocol)) {
      return url;
    }

    return '';
  } catch {
    // If URL parsing fails, it's likely malformed
    return '';
  }
}

/**
 * Validates and sanitizes an integer input
 * @param value - The value to validate
 * @param min - Minimum allowed value (default: 1)
 * @param max - Maximum allowed value (default: Number.MAX_SAFE_INTEGER)
 * @returns Valid integer or null if invalid
 */
export function sanitizeInteger(value: unknown, min: number = 1, max: number = Number.MAX_SAFE_INTEGER): number | null {
  const parsed = parseInt(String(value), 10);

  if (isNaN(parsed) || !Number.isInteger(parsed) || parsed < min || parsed > max) {
    return null;
  }

  return parsed;
}

/**
 * Validates an email address format
 * @param email - The email to validate
 * @returns True if valid email format
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) && email.length <= 254;
}

/**
 * Sanitizes user input for display purposes
 * @param input - The input to sanitize
 * @param maxLength - Maximum allowed length (default: 1000)
 * @returns Sanitized string
 */
export function sanitizeDisplayText(input: string, maxLength: number = 1000): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Trim whitespace and limit length
  const trimmed = input.trim().substring(0, maxLength);

  // Escape HTML characters
  return escapeHtml(trimmed);
}

/**
 * Validates that a string contains only safe characters for file names
 * @param filename - The filename to validate
 * @returns True if safe filename
 */
export function isSafeFilename(filename: string): boolean {
  if (!filename || typeof filename !== 'string') {
    return false;
  }

  // Allow alphanumeric, dash, underscore, and dot
  const safePattern = /^[a-zA-Z0-9._-]+$/;
  return safePattern.test(filename) &&
         filename.length <= 255 &&
         !filename.startsWith('.') &&
         !filename.endsWith('.');
}

/**
 * Sanitizes text for use in meta tags and structured data
 * @param text - The text to sanitize
 * @param maxLength - Maximum allowed length (default: 160 for meta descriptions)
 * @returns Sanitized string safe for meta tags
 */
export function sanitizeForMeta(text: string, maxLength: number = 160): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Remove HTML tags, escape quotes, and limit length
  return text
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/"/g, '&quot;')  // Escape quotes
    .replace(/'/g, '&#39;')   // Escape single quotes
    .trim()
    .substring(0, maxLength);
}

/**
 * Sanitizes structured data object to prevent XSS in JSON-LD
 * @param data - The structured data object to sanitize
 * @returns Sanitized object safe for JSON-LD
 */
export function sanitizeStructuredData(data: unknown): Record<string, unknown> | unknown[] | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeStructuredData(item));
  }

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeForMeta(value, 1000); // Longer limit for structured data
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeStructuredData(value);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value;
    }
    // Skip other types (functions, undefined, etc.)
  }

  return sanitized;
}